import type { AxiosError } from 'axios';

import { defaultErrorHandler } from '@/services';
import {
  type CustomAccessRightsKey,
  useCustomAccessRights,
} from '@/stores/accessRights/customAccessRights';

import { debug } from './logger';
import { useMainStore } from './stores/main';

export function userPermissionsHaveBeenLoaded(): boolean {
  const store = useMainStore();
  return Object.keys(store.userInfo.user).length > 0;
}

/**
 * Check if action is allowed
 * @param object e.g. "namespaces" or "policies"
 * @param verb e.g. "get" or "list"
 */
export function allowed(object: string, verb: string): boolean | undefined {
  const store = useMainStore();
  const server = store.userInfo.access?.[object]?.[verb]?.allowed;
  const detected = store.permsDetected?.[object]?.[verb]?.allowed;
  return server || detected;
}

/**
 * Check if action is denied
 * @param object e.g. "namespaces" or "policies"
 * @param verb e.g. "get" or "list"
 */
export function denied(object: string, verb: string): boolean {
  const store = useMainStore();
  const server = store.userInfo.access?.[object]?.[verb]?.allowed;
  const detected = store.permsDetected?.[object]?.[verb]?.allowed;
  return server === false || detected === false;
}

/**
 * Get reason associated with object/verb
 * @param object - Resource type e.g. "namespaces" or "policies"
 * @param verb - Action type e.g. "get" or "list"
 */
export function reason(object: string, verb: string): string | undefined {
  const store = useMainStore();
  return store.userInfo?.access?.[object]?.[verb]?.reason;
}

type DeniedAction = { accessType: string; reason: string };
type AllowedFetchActionsResult = { allowed: boolean; deniedActions: DeniedAction[] };

/**
 * Look over actions permissions and determine if user account has
 * full access to actions, partial access, or no access.
 *
 * Assumes that userInfo has been fetched.
 */
export function allowedFetchActions(): AllowedFetchActionsResult {
  const store = useMainStore();
  const response: AllowedFetchActionsResult = {
    allowed: true,
    deniedActions: [],
  };
  const permTypes = Object.keys(store.userInfo.access); // policies, backupactions, etc

  // Check for any disallowed actions
  const actions = permTypes.filter((a) => a.includes('actions'));
  actions.forEach((a) => {
    if (store.userInfo.access[a].list.allowed === false) {
      response.deniedActions.push({
        accessType: a,
        reason: store.userInfo.access[a].list.reason ?? '',
      });
    }
  });
  // If some but not all actions are disallowed, then allowed is true.
  if (response.deniedActions.length && actions.length) {
    response.allowed = response.deniedActions.length < actions.length;
  }
  if (denied('actions', 'list')) response.allowed = false;
  return response;
}

/**
 * Record a confirmed-allowed permission after a successful (200) response —
 * the positive counterpart to permissionsErrorHandler. K10 has no
 * SubjectAccessReview endpoint for these resources, so a succeeding request
 * is the only proactive way to learn the permission exists.
 *
 * Deliberately writes only customAccessRights: permsDetected stays
 * denial-only so a detected grant can never override an explicit server
 * denial through allowed().
 */
export function recordPermissionGranted(object: CustomAccessRightsKey, verb: string): void {
  useCustomAccessRights().setCustomAccessRight(verb, object, true);
}

/**
 * Record a confirmed-denied permission (401/403 observed) — counterpart to
 * recordPermissionGranted.
 */
export function recordPermissionDenied(object: CustomAccessRightsKey, verb: string): void {
  const store = useMainStore();
  store.setUserPermissionValue({ object, verb, value: false });
  useCustomAccessRights().setCustomAccessRight(verb, object, false);
}

/**
 * Eat permissions error responses and save permission setting.
 * If not permissions error, passes on to defaultErrorHandler.
 */
export function permissionsErrorHandler(
  error: AxiosError,
  object: CustomAccessRightsKey,
  verb: string,
): void {
  const statusCode = error?.response?.status;
  if (statusCode === 401 || statusCode === 403) {
    debug('Permissions denied response for', object, verb, error);
    recordPermissionDenied(object, verb);
    return;
  }
  defaultErrorHandler(error);
}
